/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/**
 * Pure helpers for the mobile session handoff flow. These are intentionally
 * side-effect free (beyond DOM parsing) so they can be unit tested in isolation
 * from the component's rendering and navigation side effects.
 */

/** The account portion of an injected guest-auth payload. */
export interface GuestAuthAccount {
    account_id: string;
}

/**
 * The base64-encoded JSON structure a native app injects as `X-Mobile-Guest-Auth`
 * (session URL hash or cookie) for an anonymous (guest) checkout session.
 */
export interface GuestAuth {
    is_guest: boolean;
    access_token: string;
    account: GuestAuthAccount;
    expiration: string;
}

/**
 * Type guard validating the decoded guest-auth payload before it is trusted.
 *
 * Mirrors the structural checks the handoff relies on: the payload must be an
 * object carrying a truthy `is_guest` flag, an `access_token`, an `account` with
 * an `account_id`, and an `expiration`. Anything else is treated as malformed.
 *
 * @param value - The result of `JSON.parse(atob(cookie))`, of unknown shape.
 * @returns `true` (narrowing to `GuestAuth`) when every required field is present.
 */
export function isValidGuestAuth(value: unknown): value is GuestAuth {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const auth = value as Record<string, unknown>;
    const account = auth.account;

    const hasValidAccount =
        typeof account === 'object' &&
        account !== null &&
        Boolean((account as Record<string, unknown>).account_id);

    return (
        Boolean(auth.is_guest) &&
        Boolean(auth.access_token) &&
        hasValidAccount &&
        Boolean(auth.expiration)
    );
}
